import { parse as parseDomain } from 'tldts'

// Subdomains that are functionally equivalent to the apex domain for analytics
// grouping and host-alias lookup (e.g. secure.wayfair.com → wayfair.com).
// Geo-code subdomains (.ca, .co.uk) are NOT in this list — they are distinct
// regional stores.
//
// Intentional divergence between normalization paths (DUP-617):
//   getAnalyticsShopKey() (Tinybird/dashboard grouping) — strips one or more
//   leading utility subdomain prefixes in this list, including www, producing
//   e.g. https://wayfair.com.
//   getShopKey() (shared exact-origin helper) — preserves persisted subdomain
//   forms like https://www.walmart.com so exact shops.key callers keep working.
//   canonicalShopKey() (storage validation helper, below) — validates and
//   returns the exact host as an HTTPS origin.
// Write-path callers that need the winning shops.key (e.g. bookmarkItem, scrapeUrl)
// should start from the raw URL origin and call resolveWinningShopKey() in
// @dupe/data/lib/shopStrategiesDb, which matches via PK or domains[] host aliases.
export const SHOP_DOMAIN_ALIAS_PREFIXES = [
  'www',
  'm',
  'mobile',
  'shop',
  'store',
  'secure',
  'wap',
] as const

export const SHOP_DOMAIN_ALIAS_PREFIX_RE = new RegExp(
  `^(${SHOP_DOMAIN_ALIAS_PREFIXES.join('|')})\\.`
)

// Storage-side gatekeeper for the `urls` and `shops` tables.
//
// The existing `isValidUrl` in helpers.ts is permissive on purpose — it's used
// by display / scraper code that wants to accept any reasonable input. Writes
// to `urls`/`shops` need a stricter contract: an http(s) URL pointing at a
// real public host that could plausibly be a retailer product page. The audit
// in DUP-615 found these patterns reaching the DB unchecked:
//
//   - `file:///var/mobile/Library/SMS/Attachments/...` (iOS local files)
//   - `http://169.254.169.254/...` (AWS metadata SSRF probes)
//   - `https://Type`, `https://chats/abc123`             (UI strings as URLs)
//   - `https://.https/...`, `https://https/...`          (concatenation bugs)
//   - `https://www.westelm.chttps`                       (typos)
//
// All of these pass `new URL()` cleanly. This module is the explicit allowlist
// that the write paths should call before touching the DB.

// `urls.url` is covered by existing PostgreSQL B-tree indexes, including
// `urls_url_idx` and the `(url, user_id)` / `(url, temp_user_id)` unique
// indexes. Keep this UTF-8 byte cap comfortably below the practical
// indexed-text limit so validation cannot accept values that later fail at
// insert/update time with an "index row size" error. This still covers the
// observed ~2150-byte real retailer URL from the DUP-616 shadow run.
export const MAX_STORABLE_URL_LENGTH = 2300

const utf8Encoder = new TextEncoder()

function utf8ByteLength(value: string): number {
  return utf8Encoder.encode(value).byteLength
}

/** Cap on favicon URLs persisted to `shops.favicon`. Real retailer
 * favicons live well under this — anything bigger is almost certainly a
 * data: URI or a malformed scraper artifact. */
export const MAX_FAVICON_URL_LENGTH = 1024

/** Cap on OG cover image URLs persisted to `shops.cover_image`. Larger
 * than favicons because retailer OG images often live behind CDNs with
 * long signed-query parameters. */
export const MAX_COVER_IMAGE_URL_LENGTH = 2048

// Shared shape check used by both image-column validators. Looser than
// `isStorableProductUrl` because image URLs may legitimately live on
// third-party CDNs, but still require a public ICANN hostname so scraped
// `localhost` / `.internal` / junk hosts cannot be persisted into shop
// metadata columns.
function isStorableImageLikeUrl(
  raw: string | null | undefined,
  maxLength: number
): boolean {
  if (!raw || raw.length > maxLength) return false
  if (/\s/.test(raw)) return false
  let parsed: URL
  try {
    parsed = new URL(raw)
  } catch {
    return false
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return false
  }
  // No userinfo — provider responses or scraper output shouldn't
  // include credentials, and `https://user:tok@cdn.example.com/og.png`
  // would silently leak the token into `shops.favicon` /
  // `shops.cover_image`.
  if (parsed.username || parsed.password) return false

  // Reject IP literals. Same threat model
  // as `isStorableProductUrl` — a compromised page or intranet
  // retailer could embed a scraped favicon at `http://169.254.169.254/`
  // and we'd happily persist it into `shops.favicon`. IPv6 brackets
  // surface as `:` in `URL.hostname` after WHATWG normalization.
  const host = parsed.hostname.toLowerCase()
  if (!host) return false
  if (host.includes(':')) return false
  const ipv4Match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.test(host)
  if (ipv4Match) return false
  if (JUNK_HOST_EXACT.has(host)) return false
  if (host.startsWith('.') || host.endsWith('.') || host.includes('..')) {
    return false
  }

  const domain = parseDomain(host)
  if (domain.isIp) return false
  if (!domain.domain || !domain.publicSuffix || !domain.isIcann) {
    return false
  }

  return true
}

// Cheap boundary check before we persist a scraped favicon URL.
export function isStorableFaviconUrl(raw: string | null | undefined): boolean {
  return isStorableImageLikeUrl(raw, MAX_FAVICON_URL_LENGTH)
}

// Validator for OG cover image URLs (`shops.cover_image`). Same rules
// as `isStorableFaviconUrl` but a larger length cap.
export function isStorableCoverImageUrl(
  raw: string | null | undefined
): boolean {
  return isStorableImageLikeUrl(raw, MAX_COVER_IMAGE_URL_LENGTH)
}

// FNV-1a 32-bit — deterministic short hash for redacting URLs in shadow
// logs. We need the hash to group repeated rejections of the same URL
// without writing the URL itself to Datadog. Crypto-strength is not
// required (no preimage-resistance need); collision rate over a 1-day
// shadow window is negligible.
//
// `Math.imul` is used instead of `*` because JS numbers are float64; for
// long inputs the intermediate `hash * 0x01000193` exceeds 2^53 and
// silently loses precision, producing different hashes than a real
// 32-bit FNV. `Math.imul` enforces int32 multiplication with wraparound,
// which is what FNV-1a expects.
function fnv1a32(input: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

// Redact a URL for shadow logging. The full URL string can contain
// session tokens, iOS local file paths (DUP-615 audit), and SSRF probe
// targets — we explicitly do NOT want any of that in Datadog. Callers
// log this redacted shape instead of the raw URL.
export interface RedactedUrl {
  /** Stable 8-char hash of the original string for log-side joining. */
  urlHash: string
  /** Hostname only — never the path, query, or userinfo. */
  hostname: string | null
  /**
   * Scheme for triage (`file`, `data`, `http`, `https`, …). Stored
   * without the trailing colon — `parsed.protocol` includes it but
   * we strip it here so log facets and Datadog filters can compare
   * directly to the literal scheme name.
   */
  scheme: string | null
  /** Total raw JS string length, retained for existing Datadog facets. */
  urlLength: number
  /** Total raw UTF-8 byte length, used for the `too_long` storage gate. */
  urlByteLength: number
}

export function redactUrlForLog(raw: string | null | undefined): RedactedUrl {
  if (!raw) {
    return {
      urlHash: '00000000',
      hostname: null,
      scheme: null,
      urlLength: 0,
      urlByteLength: 0,
    }
  }
  const urlHash = fnv1a32(raw)
  let hostname: string | null = null
  let scheme: string | null = null
  try {
    const parsed = new URL(raw)
    hostname = parsed.hostname || null
    scheme = parsed.protocol.replace(/:$/, '')
  } catch {
    // unparseable — leave hostname/scheme null
  }
  return {
    urlHash,
    hostname,
    scheme,
    urlLength: raw.length,
    urlByteLength: utf8ByteLength(raw),
  }
}

// Hosts we've seen in production that are clearly not retailer domains —
// usually leaking UI placeholders, internal IDs, or stringified types into the
// URL field. Match exactly (after lowercasing); a real retailer that happens
// to contain one of these words is unaffected.
const JUNK_HOST_EXACT = new Set([
  'https',
  'http',
  'type',
  'chats',
  'dupe',
  'null',
  'undefined',
  'true',
  'false',
])

// Reserved IPv4 ranges that should never be the destination of a stored URL.
// Cloud metadata services (169.254.169.254, 100.100.100.200) are SSRF probe
// targets; loopback / RFC1918 / link-local indicate the request never left
// the client's machine.
function isReservedIpv4(ip: string): boolean {
  const parts = ip.split('.').map(Number)
  if (
    parts.length !== 4 ||
    parts.some((p) => Number.isNaN(p) || p < 0 || p > 255)
  ) {
    return false
  }
  // Length is now guaranteed 4; only the first two octets are needed to
  // decide every reserved block below.
  const a = parts[0] as number
  const b = parts[1] as number
  return (
    a === 0 ||
    a === 10 ||
    a === 127 ||
    (a === 169 && b === 254) ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168) ||
    (a === 100 && b >= 64 && b <= 127) ||
    a >= 224 // multicast + reserved
  )
}

export type StorableUrlResult =
  | { ok: true; url: URL; canonicalShopKey: string }
  | { ok: false; reason: StorableUrlReason }

export type StorableUrlReason =
  | 'empty'
  | 'too_long'
  | 'unparseable'
  | 'bad_scheme'
  | 'ip_literal'
  | 'reserved_ip'
  | 'no_tld'
  | 'junk_host'
  | 'no_host'
  | 'has_credentials'
  | 'has_whitespace'
  | 'search_engine'
  | 'self_reference'

// Users paste the search-results page they came from (google.com/search?q=…,
// bing.com/search?q=…) instead of the product link — ~0.2% of the urls table
// per the 2026-07 audit. A SERP is never a product page, so reject before it
// mints a urls row (and a shops row for the engine's domain).
function isSearchEngineResultsUrl(parsed: URL, host: string): boolean {
  const path = parsed.pathname
  // Any google.<tld> / google.<cc>.<tld> property, incl. subdomains
  // (www., images.). Anchored so google.com.attacker.com can't match.
  if (/(^|\.)google\.(?:[a-z]{2,3}\.)?[a-z]{2,6}$/.test(host)) {
    return path === '/search' || path === '/url'
  }
  if (host === 'bing.com' || host.endsWith('.bing.com')) {
    return path === '/search' || path.startsWith('/images/search')
  }
  if (host === 'duckduckgo.com' || host.endsWith('.duckduckgo.com')) {
    return parsed.searchParams.has('q')
  }
  if (host === 'search.yahoo.com' || host.endsWith('.search.yahoo.com')) {
    return true
  }
  return false
}

// Our own web-app hosts, pasted back in by users (or echoed by internal
// flows) — 3.7K rows in the 2026-07 audit. Exact-match list on purpose:
// assets.dupe.com (ImageKit CDN) is a legitimate image-URL host for camera
// flows and must stay storable, so no blanket `.dupe.com` suffix rule.
const SELF_REFERENCE_HOSTS = new Set([
  'dupe.com',
  'www.dupe.com',
  'auth.dupe.com',
  'dupe.dev',
  'www.dupe.dev',
  // Our extension's Web Store listing — shared virally, not a product page.
  'chromewebstore.google.com',
])

// Determine whether a raw string is safe to store in `urls.url` (and to
// derive a `shops.key` from). Returns a discriminated union: callers should
// branch on `ok` and either persist (`url`, `canonicalShopKey`) or log/skip
// with `reason`.
export function isStorableProductUrl(
  raw: string | null | undefined
): StorableUrlResult {
  if (!raw) return { ok: false, reason: 'empty' }
  if (utf8ByteLength(raw) > MAX_STORABLE_URL_LENGTH) {
    return { ok: false, reason: 'too_long' }
  }
  // Two distinct WHATWG silent-normalisation hazards that create dup rows:
  //   1. Leading/trailing whitespace — new URL(' https://shop.com') succeeds
  //      and .href strips the space, so " https://shop.com" and "https://shop.com"
  //      would hash to different values but resolve identically.
  //   2. Embedded tab / LF / CR — WHATWG *strips* these (not encodes), so
  //      "https://shop.com/p\tath" → "https://shop.com/path", same dup risk.
  // Internal spaces in paths are safe — new URL() *percent-encodes* them to
  // %20, producing a stable canonical form. The original /\s/ was too broad
  // and was rejecting legitimate retailer URLs (Ballard Designs, Williams-Sonoma
  // etc. whose product paths contain spaces).
  if (/^\s|\s$/.test(raw) || /[\t\n\r]/.test(raw)) {
    return { ok: false, reason: 'has_whitespace' }
  }

  let parsed: URL
  try {
    parsed = new URL(raw)
  } catch {
    return { ok: false, reason: 'unparseable' }
  }

  // Re-check length against the canonical form: percent-encoding (e.g. space →
  // %20) can expand the URL past the limit even when the raw string passed.
  if (utf8ByteLength(parsed.href) > MAX_STORABLE_URL_LENGTH) {
    return { ok: false, reason: 'too_long' }
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { ok: false, reason: 'bad_scheme' }
  }

  // `https://user:token@shop.com/...` — embedded credentials. WHATWG
  // parses this happily and `.origin` discards the userinfo, but we'd
  // persist the raw string with the token still in it. Reject so they
  // never reach storage; the reason is distinct enough that callers can
  // log it and we can spot misbehaving integrations on the dashboard.
  if (parsed.username || parsed.password) {
    return { ok: false, reason: 'has_credentials' }
  }

  const host = parsed.hostname.toLowerCase()
  if (!host) return { ok: false, reason: 'no_host' }

  // Bracketed IPv6 (`[::1]`) — `URL.hostname` strips the brackets, but the
  // presence of `:` in the hostname (after WHATWG normalization) means v6.
  if (host.includes(':')) {
    return { ok: false, reason: 'ip_literal' }
  }

  // IPv4 literal: all-numeric dotted quad. `tldts` will tell us via `isIp`
  // but a direct check is unambiguous for the reserved-range path below.
  const ipv4Match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host)
  if (ipv4Match) {
    return {
      ok: false,
      reason: isReservedIpv4(host) ? 'reserved_ip' : 'ip_literal',
    }
  }

  if (JUNK_HOST_EXACT.has(host)) {
    return { ok: false, reason: 'junk_host' }
  }

  // Reject malformed labels: leading/trailing dot, consecutive dots, empty
  // labels. WHATWG parses `https://.westelm.com` and `https://www.westelm.`
  // without error; tldts will hand back a plausible-looking domain for both
  // because the trailing dot is treated as the DNS root and the leading dot
  // produces an empty subdomain. Neither is a real retailer URL.
  if (host.startsWith('.') || host.endsWith('.') || host.includes('..')) {
    return { ok: false, reason: 'no_tld' }
  }

  const domain = parseDomain(host)
  if (domain.isIp) {
    return { ok: false, reason: 'ip_literal' }
  }
  // `isIcann: false` covers `westelm.` (tldts treats bare `westelm` as a
  // private/non-ICANN suffix) and other made-up TLDs. We only accept hosts
  // whose public suffix is in the ICANN-managed list.
  if (!domain.domain || !domain.publicSuffix || !domain.isIcann) {
    return { ok: false, reason: 'no_tld' }
  }

  if (SELF_REFERENCE_HOSTS.has(host)) {
    return { ok: false, reason: 'self_reference' }
  }

  // Glued-host artifact from the dupe.com/<url> address-bar habit: users type
  // our domain and the retailer's with no separator ("dupe.comwayfair.com",
  // "dupe.comwww.tiffany.co.uk"), the paste flow prepends https://, and a junk
  // shop row is minted. Label-anchored (start of host or after a "."), so an
  // unrelated domain merely containing the substring (notdupe.comfoo.com)
  // can't match. If "dupe.com" is followed by more host containing another
  // dot (i.e. a second domain glued on), it can't be a real retailer.
  // A plain subdomain like dupe.com.kohler.com is excluded (rest starts with
  // ".") — canonical-key resolution collapses those onto the real brand.
  const glueMatch = /(?:^|\.)dupe\.com/.exec(host)
  if (glueMatch) {
    const rest = host.slice(glueMatch.index + glueMatch[0].length)
    if (rest && !rest.startsWith('.') && rest.includes('.')) {
      return { ok: false, reason: 'self_reference' }
    }
  }

  if (isSearchEngineResultsUrl(parsed, host)) {
    return { ok: false, reason: 'search_engine' }
  }

  return {
    ok: true,
    url: parsed,
    canonicalShopKey: `https://${host}`,
  }
}

// Legacy-named convenience wrapper for callers that only need the validated
// exact HTTPS origin and don't care about the reason for rejection.
export function canonicalShopKey(
  raw: string | null | undefined
): string | null {
  const result = isStorableProductUrl(raw)
  return result.ok ? result.canonicalShopKey : null
}
